<template>
  <div class="dash-page">
    <SnackbarComp :SnackbarComponent="snackbar" />
    <CallerIdsDialog
      v-model="showCallerIdsDialog"
      :day-label="callerIdsDayLabel"
      :caller-ids="callerIdsForDialog"
    />

    <template v-if="isSessionReady">
      <DashboardHero
        :greeting="greeting"
        :display-name="displayName"
        :kpis="heroKpis"
        :period-label="periodLabel"
      >
        <template #toolbar>
            <v-select
              v-model="selectedPeriod"
              :items="periodOptions"
              item-title="label"
              item-value="value"
              density="compact"
              variant="outlined"
              hide-details
              prepend-inner-icon="mdi-calendar-month-outline"
            class="dash-toolbar__period"
              menu-icon="mdi-chevron-down"
            :disabled="refreshing"
              @update:model-value="onPeriodChange"
            />
        </template>
      </DashboardHero>

      <nav v-if="outgoingEnabled" class="dash-tabs" role="tablist" aria-label="Dashboard sections">
        <button
          type="button"
          class="dash-tab"
          :class="{ 'is-active': pageTab === 'overview' }"
          role="tab"
          :aria-selected="pageTab === 'overview'"
          @click="pageTab = 'overview'"
        >
          Overview
        </button>
        <button
          type="button"
          class="dash-tab"
          :class="{ 'is-active': pageTab === 'campaigns' }"
          role="tab"
          :aria-selected="pageTab === 'campaigns'"
          @click="pageTab = 'campaigns'"
        >
          Campaigns
        </button>
      </nav>

      <div v-show="pageTab === 'overview'" class="dash-layout">
        <div class="dash-layout__main">
          <DashboardOperationsGrid
            :metrics="operationsMetrics"
            :loading="showSkeleton"
            @refresh="handleRefresh"
          />

          <DashboardAnalyticsSection
            v-model:chart-period="chartPeriod"
            v-model:chart-metric="chartMetric"
            :points="chartPoints"
            :period-label="periodLabel"
            :loading="showSkeleton"
          />

          <DashboardActivityList
            :rows="activityRows"
            :loading="showSkeleton"
          />
        </div>

        <aside class="dash-layout__aside">
          <DashboardFinancialPanel :metrics="financialMetrics" />
          <DashboardInsightsPanel :insights="insights" />
        </aside>
      </div>

      <div v-show="outgoingEnabled && pageTab === 'campaigns'" class="dash-campaigns-pane">
        <CampaignDashboardPanel
          :campaign-id="organization?.org_id || ''"
          :campaign-name="organization?.org_name || ''"
          scope-type="ORGANIZATION"
          :active="pageTab === 'campaigns'"
          is-admin
          show-header
          :show-title="false"
        />
      </div>
    </template>

    <div
      v-else-if="loadAttempted && !refreshing"
      class="dash-empty dash-empty--page"
    >
      <v-icon icon="mdi-view-dashboard-outline" size="40" />
      <p>No dashboard data available.</p>
    </div>
  </div>
</template>

<script setup>
import { computed, ref } from 'vue'
import SnackbarComp from '@/components/Extras/SnackbarComp.vue'
import CallerIdsDialog from '@/components/Dashboard/CallerIdsDialog.vue'
import DashboardHero from '@/components/Dashboard/DashboardHero.vue'
import DashboardOperationsGrid from '@/components/Dashboard/DashboardOperationsGrid.vue'
import DashboardFinancialPanel from '@/components/Dashboard/DashboardFinancialPanel.vue'
import DashboardAnalyticsSection from '@/components/Dashboard/DashboardAnalyticsSection.vue'
import DashboardInsightsPanel from '@/components/Dashboard/DashboardInsightsPanel.vue'
import DashboardActivityList from '@/components/Dashboard/DashboardActivityList.vue'
import CampaignDashboardPanel from '@/components/Campaigns/CampaignDashboardPanel.vue'
import { useDashboardData } from '@/composables/dashboard/useDashboardData'
import { useDashboardMetrics } from '@/composables/dashboard/useDashboardMetrics'
import { getApiMessage } from '@/mixins/Extras/ApiResponse'

defineOptions({ name: 'DashboardPage' })

const snackbar = ref({})
const showCallerIdsDialog = ref(false)
const callerIdsDayLabel = ref('')
const callerIdsForDialog = ref([])
// 'overview' = the existing billing/AI-operations summary below.
// 'campaigns' = cross-product/campaign/agent KPIs (CampaignDashboardPanel.vue,
// scoped to the whole org) -- lives here rather than on Org details, since
// this "Dashboard" page/nav item is what people actually mean by "the
// dashboard", not the org profile/settings page.
const pageTab = ref('overview')

const {
  dashboard,
  insights: dashboardInsights,
  refreshing,
  loadAttempted,
  selectedPeriod,
  periodOptions,
  chartPeriod,
  chartMetric,
  isSessionReady,
  organization,
  userName,
  showSkeleton,
  dailyRows,
  periodLabel,
  refreshDashboard,
  onPeriodChange,
} = useDashboardData()

const {
  greeting,
  displayName,
  heroKpis,
  operationsMetrics,
  financialMetrics,
  chartPoints,
  insights,
  activityRows,
} = useDashboardMetrics({
  dashboard,
  organization,
  userName,
  dailyRows,
  chartPeriod,
  chartMetric,
  insights: dashboardInsights,
})

// Same BMS-controlled gate LandingPage.vue's own nav uses to hide
// Campaigns/Contexts/BusinessQueries for orgs without outbound calling --
// the Campaigns tab here shows exactly that data, so it's meaningless (and
// would just error/render empty) for those same orgs.
const outgoingEnabled = computed(() => organization.value?.outgoing_enabled !== false)

function showError(payload) {
  snackbar.value = {
    SnackbarVmodel: true,
    SnackbarColor: 'error',
    SnackbarText: getApiMessage(payload, 'Failed to load dashboard'),
  }
}

async function handleRefresh() {
  try {
    const result = await refreshDashboard()
    if (result && result.status === 'ERROR') {
      showError(result)
    }
      } catch (err) {
    showError(err)
  }
}
</script>
